// Cause Analysis — drilldownable breakdowns by initial/root cause, unsafe
// act/condition, machinery, body part, injury, location, shift, department.
import { useEffect, useState } from "react";
import { X, Loader2, ChevronRight, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Pill from "@/components/Pill";
import { FilterBar, buildParams, dateRangeFromFilters, ChartCard, Empty, PALETTE } from "@/pages/safety/metrics/shared";
import { IncidentDialog } from "@/pages/safety/IncidentsPage";

const SEVERITY_TONE = { Minor: "neutral", Moderate: "amber", Major: "red", Fatal: "red" };

export default function CauseAnalysis({ filters, onChange, clients, buildings, projects, meta, isPortal, canEdit }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const [drill, setDrill] = useState(null);       // { title, param, value }
    const [openIncident, setOpenIncident] = useState(null);

    useEffect(() => {
        setData(null); setError(false);
        api.get("/incidents/dashboard", { params: buildParams(filters) })
            .then((r) => setData(r.data)).catch(() => setError(true));
    }, [filters]);

    const cards = data ? [
        { title: "Initial cause", param: "initial_cause", rows: data.initial_causes, color: "#2A5DB0" },
        { title: "Root cause", param: "root_cause_category", rows: data.root_causes, color: "#C75A1B" },
        { title: "Type of injury", param: "injury_nature", rows: data.injury_types, color: "#B33A2E" },
        { title: "Body part injured", param: "body_part", rows: data.by_body_part, color: "#6B4E9E" },
        { title: "Location", param: "location", rows: data.by_location, color: "#3F7D3A" },
        { title: "Shift", param: "shift", rows: data.by_shift, color: "#C68A1C" },
        { title: "Department", param: "department", rows: data.by_department, color: "#4C9A8A" },
        {
            title: "Unsafe act vs condition", param: "unsafe_classification",
            rows: Object.entries(data.unsafe_split).map(([name, count]) => ({ name, count })).filter((r) => r.count), color: "#D9772E",
        },
        {
            title: "Machinery / equipment involvement", param: "machinery",
            rows: Object.entries(data.machinery).map(([name, count]) => ({ name, count, drillValue: name === "Involved" ? "yes" : "no" })).filter((r) => r.count), color: "#8A6D3B",
        },
    ] : [];

    return (
        <div data-testid="cause-analysis" className="space-y-5">
            <FilterBar filters={filters} onChange={onChange} clients={clients} buildings={buildings}
                projects={projects} meta={meta} isPortal={isPortal} />
            {error ? (
                <div className="text-[#9A968F] italic py-12 text-center">Couldn't load cause analysis.</div>
            ) : !data ? (
                <div className="text-[#9A968F] italic py-12 text-center">Loading cause analysis…</div>
            ) : (
                <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-5">
                    {cards.map((c) => (
                        <ChartCard key={c.title} testid={`cause-card-${c.param}`} title={c.title} subtitle="Click a row to view incidents">
                            <BarList rows={c.rows} color={c.color}
                                onSelect={(name, drillValue) => setDrill({ title: `${c.title}: ${name}`, param: c.param, value: drillValue ?? name })} />
                        </ChartCard>
                    ))}
                </div>
            )}

            {drill && (
                <DrilldownModal drill={drill} filters={filters} clients={clients} buildings={buildings}
                    onClose={() => setDrill(null)} onOpenIncident={(inc) => setOpenIncident(inc)} />
            )}
            {openIncident && (
                <IncidentDialog incident={openIncident} meta={meta} clients={clients} buildings={buildings}
                    portalClientId={isPortal ? filters.client_id : null} viewerCanEdit={canEdit}
                    onClose={() => setOpenIncident(null)} onSaved={() => setOpenIncident(null)} />
            )}
        </div>
    );
}

function BarList({ rows, color, onSelect }) {
    if (!rows || rows.length === 0) return <Empty />;
    const data = rows.slice(0, 12);
    const max = Math.max(...data.map((r) => r.count), 1);
    return (
        <div className="space-y-1.5" data-testid="cause-barlist">
            {data.map((r) => (
                <button key={r.name} data-testid={`cause-bar-${r.name}`}
                    onClick={() => onSelect(r.name, r.drillValue)}
                    className="w-full group flex items-center gap-3 text-left rounded-lg px-1.5 py-1 hover:bg-[#F6F4F0] transition">
                    <span className="text-[12px] text-[#3e4b63] w-[120px] truncate shrink-0">{r.name}</span>
                    <span className="flex-1 h-2.5 rounded-full bg-[#F0EDE8] overflow-hidden">
                        <span className="block h-full rounded-full" style={{ width: `${(r.count / max) * 100}%`, background: color }} />
                    </span>
                    <span className="text-[12px] font-semibold text-[#1F1F1F] w-7 text-right tabular-nums">{r.count}</span>
                    <ChevronRight size={13} className="text-[#C0BBB3] group-hover:text-[var(--brand-accent,#C75A1B)]" />
                </button>
            ))}
        </div>
    );
}

function DrilldownModal({ drill, filters, clients, buildings, onClose, onOpenIncident }) {
    const [rows, setRows] = useState(null);
    const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));

    useEffect(() => {
        const params = { ...dateRangeFromFilters(filters) };
        if (filters.client_id) params.client_id = filters.client_id;
        if (filters.building_id) params.building_id = filters.building_id;
        params[drill.param] = drill.value;
        api.get("/incidents", { params }).then((r) => setRows(r.data))
            .catch((e) => { toast.error(formatApiError(e.response?.data?.detail) || "Could not load"); setRows([]); });
    }, [drill, filters]);

    async function open(id) {
        try { const { data } = await api.get(`/incidents/${id}`); onOpenIncident(data); }
        catch (e) { toast.error("Could not open incident"); }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" data-testid="cause-drilldown" onClick={onClose}>
            <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-start justify-between sticky top-0 bg-white">
                    <div>
                        <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#9A968F]">Incidents</div>
                        <h3 className="text-[15px] font-bold text-[#1F1F1F]">{drill.title}</h3>
                        <div className="text-[11.5px] text-[#9A968F] mt-0.5">{rows == null ? "Loading…" : `${rows.length} matching`}</div>
                    </div>
                    <button onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                <div className="p-4 space-y-2">
                    {rows == null ? (
                        <div className="text-center py-10 text-[#9A968F]"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</div>
                    ) : rows.length === 0 ? (
                        <Empty label="No incidents match this selection." />
                    ) : rows.map((r) => (
                        <button key={r.id} data-testid={`drill-incident-${r.id}`} onClick={() => open(r.id)}
                            className="w-full text-left rounded-lg border border-[#E5E0DA] hover:border-[var(--brand-accent,#C75A1B)] p-3 transition">
                            <div className="flex items-center justify-between">
                                <span className="font-mono text-[11px] text-[#9A968F]">{r.reference}</span>
                                <div className="flex items-center gap-1.5">
                                    {r.riddor_reportable && <ShieldAlert size={12} className="text-red-600" />}
                                    <Pill tone={SEVERITY_TONE[r.severity] || "neutral"}>{r.severity}</Pill>
                                </div>
                            </div>
                            <div className="font-semibold text-[13px] text-[#1F1F1F] mt-1">{r.incident_type} · {r.location || "—"}</div>
                            <div className="text-[11.5px] text-[#6F6A64] mt-0.5">{clientMap[r.client_id]?.name} · {(r.occurred_at || "").slice(0, 10)}</div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
